import React, {Suspense, useEffect, useState} from 'react';
import {BrowserRouter as Router, Link, Redirect, Route, Switch} from "react-router-dom";
import SideNav from "../components/layouts/sidebar";
import {Button, Col, Layout, Popover, Row, Spin, Tooltip} from 'antd';
import {
    FileSearchOutlined,
    LogoutOutlined,
    MenuFoldOutlined,
    MenuUnfoldOutlined,
    QuestionCircleOutlined,
    SettingOutlined,
    TeamOutlined
} from '@ant-design/icons';
import {session} from "../service/session";
import AccountSelect from "../components/pages/account/accountSelect";
import GEOKON_BLUE from "../index";
import AdminSessionNotification from "../components/pages/admin/util/adminSessionNotification";

const AdminPanel = React.lazy(() => import("../components/pages/admin/adminPanel"))
const DevicesPanel = React.lazy(() => import("../components/pages/device/devices"))
const Settings = React.lazy(() => import("../components/pages/settings"))
const ConnectionsPage = React.lazy(() => import("../components/pages/connections/connectionsPanel"))
const BillingPanel = React.lazy(() => import("../components/pages/billing/billingPanel"))
const LoggerConfig = React.lazy(() => import("../components/pages/device/clientLoggerConfig"))
const DataPanel = React.lazy(() => import("../components/pages/data/dataPanel"))

const { Header, Sider, Content} = Layout;

const shortcutIconStyles = {
    marginLeft: 8
}

const ApplicationRoutes = () => {

    const isDarkSession = session.isDarkSession();
    const demoCollapse =  window.innerWidth > 760 && !session.isContinuedSession()
    const [collapse, setCollapse] = useState(!demoCollapse);
    useEffect(() => {
        if (demoCollapse)
            setTimeout(() => setCollapse(true), 1500);
    }, []);
    const handleToggle = (event: any) => {
        event.preventDefault();
        collapse ? setCollapse(false) : setCollapse(true);
    }

    const StatusBadgeIframe = ({ width = "25", height = "30", style = {} }) => {
        return process.env.REACT_APP_STATUS_BADGE_URL ? <iframe
            src={`${process.env.REACT_APP_STATUS_BADGE_URL}theme=${isDarkSession ? 'dark' : 'light'}`}
            width={width}
            height={height}
            frameBorder="0"
            scrolling="no"
            style={{
                border: 'none',
                margin: 0,
                padding: 0,
                ...style
            }}
        /> : null
    };

    return (
        <Router>
            <Layout>
                {session.isAdminSession() ? <AdminSessionNotification/> : null}

                <Sider theme="light" trigger={null} collapsible collapsed={collapse} style={{ position: 'relative' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                        <div style={{ flexShrink: 0 }}>
                            <SideNav />
                        </div>
                        <div style={{ flexGrow: 1, display: 'flex', alignItems: 'stretch', justifyContent: 'center' }}>
                            <Button
                                type="text"
                                size="large"
                                icon={collapse ? <MenuUnfoldOutlined style={{ fontSize: "25px" }} /> : <MenuFoldOutlined style={{ fontSize: "25px" }} />}
                                onClick={handleToggle}
                                style={{ 
                                    width: '100%',
                                    height: '100%', 
                                    padding: '0',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'flex-end',
                                    paddingBottom: '30px',
                                    background: 'none'
                                }}
                            />
                        </div>
                    </div>
                </Sider>
                <Layout style={session.isAdminSession() ? {background: GEOKON_BLUE.replace(',1)', ',0.5')} : {background: (session.isDarkSession() ? 'rgb(30,30,30)': '')}}>
                    <Header className="siteLayoutBackground" style={{padding: 0, background: (session.getUserSettings().darkMode ? '#141414' : 'white')}}>
                        <Row justify="end">
                            <Col span={24} style={{textAlign: 'right', paddingRight: 50}}>
                                <Popover
                                    placement={'left'}
                                    content={<StatusBadgeIframe width="185" height="30" style={{display: 'block'}}/>}
                                    overlayInnerStyle={{ border: 'none', padding: '4px'}}
                                >
                                    <div style={{ display: 'inline-block' }}>
                                        <StatusBadgeIframe style={{ borderRadius: '15px', verticalAlign: 'middle', marginRight: '8px' }} />
                                    </div>
                                </Popover>

                                <Tooltip title={'Viewing API Account:'} placement={'left'}>
                                    <div style={{display: 'inline', textAlign: "left", paddingRight: 5}}>
                                        <AccountSelect/>
                                    </div>
                                </Tooltip>

                                <Popover content={
                                    <>
                                        <Tooltip title={"View our documentation"}>
                                            <Button icon={<FileSearchOutlined />} type={"primary"} onClick={() => window.open("https://apidocs.geokon.com", "_blank")} style={{marginRight: 10}}>
                                                API Docs
                                            </Button>
                                        </Tooltip>
                                        <Tooltip title={"Contact our technical support team"}>
                                            <Button icon={<TeamOutlined />} type={"primary"} onClick={() => window.open('https://www.geokon.com/Technical-Support', '_blank')}>
                                                Get Support
                                            </Button>
                                        </Tooltip>
                                    </>
                                }>
                                    <Button type="primary" shape="circle" icon={<QuestionCircleOutlined/>} style={shortcutIconStyles}/>
                                </Popover>
                                <Tooltip title={"Settings"}>
                                    <Link to={"/settings"}>
                                        <Button type="primary" shape="circle" icon={<SettingOutlined/>} style={shortcutIconStyles}/>
                                    </Link>
                                </Tooltip>
                                <Tooltip title={"Log out"}>
                                    <Button type="primary" shape="circle" icon={<LogoutOutlined/>} style={shortcutIconStyles}
                                            onClick={session.endSession}/>
                                </Tooltip>
                            </Col>
                        </Row>
                    </Header>
                    <Content style={{
                        margin: '20px 16px 0px 16px',
                        padding: '16px 20px 0px 20px',
                        height: "calc(100vh - 84px)",
                        background: session.getUserSettings()?.darkMode ? 'black' : "#fff",
                        overflow: "auto"
                    }}>
                        <Switch>
                            <Route path="/settings"><Suspense fallback={<Spin/>}><Settings/></Suspense></Route>
                            <Route path="/devices"><Suspense fallback={<Spin/>}><DevicesPanel/></Suspense></Route>
                            <Route path="/device/*"><Suspense fallback={<Spin/>}><LoggerConfig/></Suspense></Route>
                            <Route path="/connections"><Suspense fallback={<Spin/>}><ConnectionsPage/></Suspense></Route>
                            <Route path="/billing"><Suspense fallback={<Spin/>}><BillingPanel/></Suspense></Route>
                            <Route path="/admin"><Suspense fallback={<Spin/>}><AdminPanel/></Suspense></Route>
                            <Route path="/data"><Suspense fallback={<Spin/>}><DataPanel/></Suspense></Route>
                            <Redirect to="/devices" from="/"/>
                        </Switch>

                    </Content>
                </Layout>
            </Layout>
        </Router>
    );
}

export default ApplicationRoutes;